import { MAX_BONDS, MAX_BONDS_PER_SLIME, MAX_SLIMES } from '../../engine/core/constants';

/**
 * 黏连的绑定表：预分配定长，不用原型那样的对象数组加线性扫描
 * （[规则与行为](../../../docs/架构/规则与行为实现规范.md)禁止闭包隐藏每实体状态）。
 *
 * 两份互为索引的结构：
 * - 绑定表 `a/b/until`，按下标连续排列，删除用末尾换位，因此下标不稳定；
 * - 每只的伙伴表 `partners`，固定 `MAX_BONDS_PER_SLIME` 个槽，存伙伴槽位而非绑定下标——
 *   这样换位删除不会让伙伴表失效，而「是否已绑定」只需扫本只的几个槽。
 *
 * 只有 `degree` 被别的机制读取（压力的保压）。渲染读 `a/b/count` 画线，那是结构性只读。
 */
export class BondState {
  readonly a = new Int16Array(MAX_BONDS);
  readonly b = new Int16Array(MAX_BONDS);
  /** 到期步序。用步计数而非秒，回放时不受显示帧率影响。 */
  readonly until = new Float32Array(MAX_BONDS);
  count = 0;

  /** 每只当前有效的绑定数，即原型的 `links`。 */
  readonly degree = new Uint8Array(MAX_SLIMES);
  readonly partners = new Int16Array(MAX_SLIMES * MAX_BONDS_PER_SLIME);
  /** 松开后的冷却截止步序。没有冷却，仍贴着的一对会在松开的同一步重新粘上。 */
  readonly cooldownUntil = new Float32Array(MAX_SLIMES);
  /** 上次见到的槽位世代，变化即换了占用者。 */
  readonly generation = new Uint16Array(MAX_SLIMES);

  isBonded(x: number, y: number): boolean {
    const base = x * MAX_BONDS_PER_SLIME;
    for (let k = 0; k < this.degree[x]; k += 1) if (this.partners[base + k] === y) return true;
    return false;
  }

  /** 返回 false 表示表满或任一方伙伴已满；调用方按「这一步没粘上」处理，下一步重试。 */
  add(x: number, y: number, until: number): boolean {
    if (this.count >= MAX_BONDS || this.degree[x] >= MAX_BONDS_PER_SLIME || this.degree[y] >= MAX_BONDS_PER_SLIME) return false;
    this.a[this.count] = x; this.b[this.count] = y; this.until[this.count] = until; this.count += 1;
    this.partners[x * MAX_BONDS_PER_SLIME + this.degree[x]] = y; this.degree[x] += 1;
    this.partners[y * MAX_BONDS_PER_SLIME + this.degree[y]] = x; this.degree[y] += 1;
    return true;
  }

  /** 末尾换位删除。调用方若在正向遍历中删除，须在同一下标上重新检查换进来的那条。 */
  removeAt(index: number): void {
    const x = this.a[index]; const y = this.b[index];
    this.unlink(x, y); this.unlink(y, x);
    const last = this.count - 1;
    this.a[index] = this.a[last]; this.b[index] = this.b[last]; this.until[index] = this.until[last];
    this.count = last;
  }

  clear(): void {
    this.count = 0; this.degree.fill(0); this.cooldownUntil.fill(0);
  }

  private unlink(owner: number, partner: number): void {
    const base = owner * MAX_BONDS_PER_SLIME; const degree = this.degree[owner];
    for (let k = 0; k < degree; k += 1) {
      if (this.partners[base + k] !== partner) continue;
      this.partners[base + k] = this.partners[base + degree - 1]; this.degree[owner] = degree - 1;
      return;
    }
  }
}
